/**
 * Plan complexe — logique pure (Boîte à outils, Tle → prépa).
 *
 * Les points se posent au demi-nœud : a et b sont des multiples de 0,5, donc
 * tout reste EXACT — module k√r/2 simplifié, argument exact quand il est
 * multiple de π/4 (seuls angles remarquables qu'atteint ce quadrillage),
 * produits et quotients en fractions. Racines n-ièmes de l'unité exactes pour
 * n = 2, 3, 4, 6, 8, 12 ; équation du second degré à coefficients entiers.
 */

import { exactValues, radianLabel } from '../trig-circle/trig-values';
import { simplify as sqrtSimplify } from '../root-square/roots';
import { frac, rational, type Frac } from '../../ui/exact';

export interface Z {
  a: number;
  b: number;
}

export interface Step {
  tex: string;
  say: string;
}

const MINUS = '−';
const gcd = (x: number, y: number): number => {
  x = Math.abs(x);
  y = Math.abs(y);
  while (y) [x, y] = [y, x % y];
  return x || 1;
};

/** nombre exact (fraction ou décimal fini) en LaTeX */
export const qTex = (f: Frac) => rational(f.p, f.q).tex;
const fromNum = (v: number): Frac => frac(Math.round(v * 4), 4);

/** a + ib en LaTeX : « 1{,}5 - 2i », « i », « -\frac{1}{2}i », « 0 » */
export function zTexQ(re: Frac, im: Frac): string {
  const r = re.p === 0 ? '' : qTex(re);
  if (im.p === 0) return r || '0';
  const absIm = frac(Math.abs(im.p), im.q);
  const coef = absIm.p === 1 && absIm.q === 1 ? '' : qTex(absIm);
  const imPart = `${coef}i`;
  if (!r) return im.p < 0 ? `-${imPart}` : imPart;
  return `${r} ${im.p < 0 ? '-' : '+'} ${imPart}`;
}
export const zTex = (z: Z) => zTexQ(fromNum(z.a), fromNum(z.b));

/** texte simple : « 1,5 − 2i » */
export function zPlain(z: Z): string {
  return zTex(z)
    .replace(/\{,\}/g, ',')
    .replace(/\\frac\{(\d+)\}\{(\d+)\}/g, '$1/$2')
    .replace(/-/g, MINUS);
}

/* ------------------------------------------------------------------ */
/* module et argument                                                  */
/* ------------------------------------------------------------------ */

/** |z| exact : k√r / d, pour z = a + ib au demi-nœud */
export const modulus = (z: Z) => modulusQ(fromNum(z.a), fromNum(z.b));

/**
 * |re + i·im| exact pour re et im rationnels : |z|² = P/Q réduit, donc
 * |z| = √(PQ) / Q = k√r / d (r sans facteur carré, k et d premiers entre eux).
 */
export function modulusQ(
  re: Frac,
  im: Frac,
): { tex: string; value: number; k: number; r: number; d: number } {
  const s2 = frac(re.p * re.p * im.q * im.q + im.p * im.p * re.q * re.q, re.q * re.q * im.q * im.q);
  const value = Math.sqrt(s2.p / s2.q);
  if (s2.p === 0) return { tex: '0', value: 0, k: 0, r: 1, d: 1 };
  const { k, b: r } = sqrtSimplify(s2.p * s2.q);
  const g = gcd(k, s2.q);
  const kk = k / g;
  const d = s2.q / g;
  if (r === 1) return { tex: qTex(frac(kk, d)), value, k: kk, r, d };
  const root = `\\sqrt{${r}}`;
  const num = kk === 1 ? root : `${kk}${root}`;
  return { tex: d === 1 ? num : `\\frac{${num}}{${d}}`, value, k: kk, r, d };
}

type Mod = ReturnType<typeof modulusQ>;

/** |z| en texte simple : « 5 », « 2,5 », « 2√2 », « 3√2/2 » */
export function modPlain(m: Mod): string {
  if (m.r === 1) return rational(m.k, m.d).plain;
  return `${m.k === 1 ? '' : m.k}√${m.r}${m.d === 1 ? '' : `/${m.d}`}`;
}

/** |z| en atomes de la feuille : ['3', '√', '2', '÷', '2'] */
export function modAtoms(m: Mod): string[] {
  if (m.r === 1) return m.d === 1 ? [String(m.k)] : [String(m.k), '÷', String(m.d)];
  return [
    ...(m.k === 1 ? [] : [String(m.k)]),
    '√',
    String(m.r),
    ...(m.d === 1 ? [] : ['÷', String(m.d)]),
  ];
}

/** nombre du quadrillage (multiple de 0,25) en LaTeX : « 1{,}5 », « -2 » */
export const numTex = (v: number) => qTex(fromNum(v));
const sqTex = (v: number) => (v < 0 ? `(${numTex(v)})^2` : `${numTex(v)}^2`);

/** |z| = √(a² + b²) = √… = … (sans répéter une écriture identique) */
export function modulusTex(z: Z): string {
  const m = modulus(z);
  const inside = qTex(frac((2 * z.a) ** 2 + (2 * z.b) ** 2, 4));
  const parts = [`\\sqrt{a^2 + b^2}`, `\\sqrt{${sqTex(z.a)} + ${sqTex(z.b)}}`, `\\sqrt{${inside}}`];
  if (m.tex !== `\\sqrt{${inside}}`) parts.push(m.tex);
  return `|z| = ${parts.join(' = ')}`;
}

/** argument principal dans ]−π ; π] : exact (multiple de π/4) ou approché */
export function argument(z: Z): {
  exact: boolean;
  tex: string;
  plain: string;
  value: number;
  step: number | null;
} {
  const value = Math.atan2(z.b, z.a);
  if (z.a === 0 && z.b === 0) return { exact: false, tex: '', plain: '', value: 0, step: null };
  if (z.a === 0 || z.b === 0 || Math.abs(z.a) === Math.abs(z.b)) {
    // en pas de 15° : multiple de 3 (45°)
    let k = Math.round((value * 12) / Math.PI);
    if (k === -12) k = 12;
    const l = radianLabel(k);
    return { exact: true, tex: l.tex, plain: l.plain, value, step: k };
  }
  const deg = (value * 180) / Math.PI;
  const t = (v: number, d: number) => Number(v.toFixed(d)).toString().replace('.', '{,}');
  return {
    exact: false,
    tex: `\\approx ${t(value, 3)}\\ \\text{rad} \\approx ${t(deg, 1)}^\\circ`,
    plain: `≈ ${Number(value.toFixed(3)).toString().replace('.', ',').replace('-', MINUS)} rad`,
    value,
    step: null,
  };
}

/** écriture trigonométrique et exponentielle */
export function polarForms(z: Z): { trig: string; expo: string } | null {
  if (z.a === 0 && z.b === 0) return null;
  const m = modulus(z);
  const arg = argument(z);
  const r = m.tex === '1' ? '' : m.tex;
  if (arg.exact) {
    const th = arg.tex;
    return {
      trig: `${r}\\left(\\cos ${wrap(th)} + i \\sin ${wrap(th)}\\right)`,
      expo: `${r}\\,${expTex(th)}`,
    };
  }
  return {
    trig: `${r}\\left(\\cos \\theta + i \\sin \\theta\\right)`,
    expo: `${r}\\,e^{i\\theta}`,
  };
}
/** e^{iθ} : « e^{i\frac{\pi}{4}} », « e^{-i\frac{3\pi}{4}} » */
export const expTex = (th: string) =>
  th.startsWith('-') ? `e^{-i ${th.slice(1)}}` : `e^{i ${th}}`;
const wrap = (t: string) => (t.startsWith('-') || t.includes('frac') ? `\\left(${t}\\right)` : t);

/** cos θ = a/|z|, sin θ = b/|z| (non rationalisés : la lecture directe) */
export function cosSin(z: Z): { cos: string; sin: string } | null {
  const m = modulus(z);
  if (m.value === 0) return null;
  const over = (v: number) => {
    const q = fromNum(v);
    if (m.r === 1) return qTex(frac(q.p * m.d, q.q * m.k));
    // v / (k√r/d) = v·d / (k√r)
    const top = frac(q.p * m.d, q.q * m.k);
    return `${top.p < 0 ? '-' : ''}\\frac{${qTex(frac(Math.abs(top.p), top.q))}}{\\sqrt{${m.r}}}`;
  };
  return { cos: over(z.a), sin: over(z.b) };
}

/* ------------------------------------------------------------------ */
/* opérations                                                          */
/* ------------------------------------------------------------------ */

export type Op = 'sum' | 'product' | 'quotient' | 'distance';

export function operate(op: Op, z1: Z, z2: Z): { re: Frac; im: Frac } | null {
  const [a, b, c, d] = [z1.a, z1.b, z2.a, z2.b].map(fromNum) as [Frac, Frac, Frac, Frac];
  const add = (x: Frac, y: Frac) => frac(x.p * y.q + y.p * x.q, x.q * y.q);
  const sub = (x: Frac, y: Frac) => frac(x.p * y.q - y.p * x.q, x.q * y.q);
  const mul = (x: Frac, y: Frac) => frac(x.p * y.p, x.q * y.q);
  const div = (x: Frac, y: Frac) => frac(x.p * y.q, x.q * y.p);
  if (op === 'sum') return { re: add(a, c), im: add(b, d) };
  if (op === 'product') return { re: sub(mul(a, c), mul(b, d)), im: add(mul(a, d), mul(b, c)) };
  if (op === 'quotient') {
    const n2 = add(mul(c, c), mul(d, d));
    if (n2.p === 0) return null;
    return {
      re: div(add(mul(a, c), mul(b, d)), n2),
      im: div(sub(mul(b, c), mul(a, d)), n2),
    };
  }
  return { re: sub(c, a), im: sub(d, b) };
}

export function opSteps(op: Op, z1: Z, z2: Z): Step[] {
  const r = operate(op, z1, z2);
  const t1 = zTex(z1);
  const t2 = zTex(z2);
  if (!r) return [{ tex: `z_2 = 0`, say: 'On ne divise pas par 0.' }];
  const res = zTexQ(r.re, r.im);
  if (op === 'sum')
    return [
      {
        tex: `z_1 + z_2 = (${t1}) + (${t2}) = ${res}`,
        say: 'On additionne les parties réelles entre elles et les parties imaginaires entre elles : c’est la règle du parallélogramme.',
      },
    ];
  if (op === 'product') {
    const m1 = modulus(z1);
    const m2 = modulus(z2);
    return [
      {
        tex: `z_1 z_2 = (${t1})(${t2}) = ${res}`,
        say: 'On développe, avec i² = −1.',
      },
      {
        tex: `|z_1 z_2| = |z_1| \\times |z_2| = ${m1.tex} \\times ${m2.tex} = ${modulusQ(r.re, r.im).tex}`,
        say: 'Les modules se multiplient : multiplier par z₂, c’est multiplier les distances à O par |z₂|…',
      },
      ...argSteps(z1, z2, 1),
    ];
  }
  if (op === 'quotient') {
    const m1 = modulus(z1);
    const m2 = modulus(z2);
    return [
      {
        tex: `\\frac{z_1}{z_2} = \\frac{z_1 \\overline{z_2}}{z_2 \\overline{z_2}} = \\frac{z_1 \\overline{z_2}}{|z_2|^2} = ${res}`,
        say: 'On multiplie en haut et en bas par le conjugué du dénominateur : le dénominateur devient réel.',
      },
      {
        tex: `\\left|\\frac{z_1}{z_2}\\right| = \\frac{|z_1|}{|z_2|} = \\frac{${m1.tex}}{${m2.tex}} = ${modulusQ(r.re, r.im).tex}`,
        say: 'Les modules se divisent…',
      },
      ...argSteps(z1, z2, -1),
    ];
  }
  const d = modulusQ(r.re, r.im);
  return [
    {
      tex: `z_2 - z_1 = ${res}`,
      say: 'Le vecteur qui va de M₁ à M₂ a pour affixe z₂ − z₁.',
    },
    {
      tex: `M_1M_2 = |z_2 - z_1| = ${d.tex}`,
      say: 'La distance entre deux points est le module de la différence de leurs affixes.',
    },
  ];
}

const toZ = (r: { re: Frac; im: Frac }): Z => ({ a: r.re.p / r.re.q, b: r.im.p / r.im.q });

const degTex = (v: number) => `${numTex1(v)}^\\circ`;
const numTex1 = (v: number) => {
  const r = Number(v.toFixed(1));
  return String(Object.is(r, -0) ? 0 : r).replace('.', '{,}');
};

/**
 * arg(z₁z₂) = arg z₁ + arg z₂ (sign = 1) ou arg(z₁/z₂) = arg z₁ − arg z₂
 * (sign = −1) : exact quand les deux arguments le sont, en degrés sinon.
 */
function argSteps(z1: Z, z2: Z, sign: 1 | -1): Step[] {
  const isZero = (z: Z) => z.a === 0 && z.b === 0;
  if (isZero(z1) || isZero(z2)) return [];
  const g1 = argument(z1);
  const g2 = argument(z2);
  const lhs = sign === 1 ? '\\arg(z_1 z_2)' : '\\arg\\left(\\frac{z_1}{z_2}\\right)';
  const op = sign === 1 ? '+' : '-';
  // égalité des arguments principaux : vraie seulement à 2π près
  const rule = `${lhs} \\equiv \\arg z_1 ${op} \\arg z_2`;
  const say =
    sign === 1
      ? '… et les arguments s’ajoutent : multiplier par z₂, c’est aussi tourner de arg z₂.'
      : '… et les arguments se soustraient.';
  if (g1.step !== null && g2.step !== null) {
    const raw = g1.step + sign * g2.step;
    let k = ((raw % 24) + 24) % 24;
    if (k > 12) k -= 24;
    const t2 = g2.tex.startsWith('-') ? `\\left(${g2.tex}\\right)` : g2.tex;
    const sum = radianLabel(raw).tex;
    const principal = radianLabel(k).tex;
    const tail = raw === k ? `= ${sum}\\ [2\\pi]` : `= ${sum} \\equiv ${principal}\\ [2\\pi]`;
    return [{ tex: `${rule} = ${g1.tex} ${op} ${t2} ${tail}`, say }];
  }
  const d1 = Number(((g1.value * 180) / Math.PI).toFixed(1));
  const d2 = Number(((g2.value * 180) / Math.PI).toFixed(1));
  const raw = Number((d1 + sign * d2).toFixed(1));
  let p = raw;
  while (p > 180) p -= 360;
  while (p <= -180) p += 360;
  p = Number(p.toFixed(1));
  const t2 = d2 < 0 ? `(${degTex(d2)})` : degTex(d2);
  const tail =
    p === raw
      ? `= ${degTex(raw)}\\ [360^\\circ]`
      : `= ${degTex(raw)} \\equiv ${degTex(p)}\\ [360^\\circ]`;
  return [{ tex: `${rule} \\approx ${degTex(d1)} ${op} ${t2} ${tail}`, say }];
}

/* ------------------------------------------------------------------ */
/* racines n-ièmes de l'unité                                          */
/* ------------------------------------------------------------------ */

/** e^{iθ} en écriture algébrique exacte (θ = k15 × 15°, remarquable) ou null */
export function unitTex(k15: number): string | null {
  const exact = exactValues(k15);
  if (!exact) return null;
  const re = exact.cos.tex;
  const im = exact.sin.tex;
  const imPart =
    exact.sin.value === 0
      ? ''
      : exact.sin.value === 1
        ? 'i'
        : exact.sin.value === -1
          ? '-i'
          : im.startsWith('-')
            ? `-${im.slice(1)}\\,i`
            : `${im}\\,i`;
  if (exact.cos.value === 0) return imPart || '0';
  if (!imPart) return re;
  return `${re} ${imPart.startsWith('-') ? '-' : '+'} ${imPart.replace(/^-/, '')}`;
}

export function unityRoot(n: number, k: number): { tex: string; exact: string | null; point: Z } {
  const angle = (2 * Math.PI * k) / n;
  const point = { a: Math.cos(angle), b: Math.sin(angle) };
  const steps15 = (360 * k) / n / 15;
  const exact = Number.isInteger(steps15) ? unitTex(steps15) : null;
  return { tex: k === 0 ? '1' : `e^{i ${reduceAngle(k, n)}}`, exact, point };
}

/** 2kπ/n réduit en LaTeX : « \frac{2\pi}{3} », « \pi » */
function reduceAngle(k: number, n: number): string {
  const g = gcd(2 * k, n);
  const p = (2 * k) / g;
  const q = n / g;
  const num = p === 1 ? '\\pi' : `${p}\\pi`;
  return q === 1 ? num : `\\frac{${num}}{${q}}`;
}

/* ------------------------------------------------------------------ */
/* équation du second degré à coefficients réels (entiers)            */
/* ------------------------------------------------------------------ */

/** p/q en LaTeX avec signe */
const fracTex = (p: number, q: number) => qTex(frac(p, q));

/** k√r / q en LaTeX (q > 0), réduit ; rationnel → décimal ou fraction comme ailleurs */
function surdTex(k: number, r: number, q: number): string {
  if (r === 1) return qTex(frac(k, q));
  const g = gcd(k, q);
  const kk = k / g;
  const qq = q / g;
  const num = kk === 1 ? `\\sqrt{${r}}` : `${kk}\\sqrt{${r}}`;
  return qq === 1 ? num : `\\frac{${num}}{${qq}}`;
}

export function solveQuadratic(
  a: number,
  b: number,
  c: number,
): {
  delta: number;
  roots: Z[];
  /** écritures exactes des racines, dans l'ordre de `roots` */
  rootTex: string[];
  steps: Step[];
} {
  const delta = b * b - 4 * a * c;
  const den = 2 * a;
  const steps: Step[] = [
    {
      tex: `\\Delta = b^2 - 4ac = ${b < 0 ? `(${b})` : b}^2 - 4 \\times ${a < 0 ? `(${a})` : a} \\times ${c < 0 ? `(${c})` : c} = ${delta}`,
      say: 'Le discriminant décide de tout.',
    },
  ];
  const sgnDen = den < 0 ? -1 : 1;
  const re = frac(-b * sgnDen, Math.abs(den));
  if (delta === 0) {
    steps.push({
      tex: `z_0 = -\\frac{b}{2a} = ${fracTex(-b, den)}`,
      say: 'Δ = 0 : une seule racine, réelle (double).',
    });
    return { delta, roots: [{ a: re.p / re.q, b: 0 }], rootTex: [fracTex(-b, den)], steps };
  }
  const { k, b: r } = sqrtSimplify(Math.abs(delta));
  const s = surdTex(k, r, Math.abs(den));
  const reTex = re.p === 0 ? '' : qTex(re);
  const root = Math.sqrt(Math.abs(delta)) / Math.abs(den);
  if (delta > 0) {
    // racines dans l'ordre croissant : z₁ < z₂, sur la figure comme dans le texte
    let lo = join(reTex, '-', s);
    let hi = join(reTex, '+', s);
    if (r === 1) {
      const x1 = frac((-b - k) * sgnDen, Math.abs(den));
      const x2 = frac((-b + k) * sgnDen, Math.abs(den));
      [lo, hi] = x1.p / x1.q < x2.p / x2.q ? [qTex(x1), qTex(x2)] : [qTex(x2), qTex(x1)];
    }
    steps.push({
      tex:
        r === 1
          ? `z_{1,2} = \\frac{-b \\pm \\sqrt{\\Delta}}{2a} \\qquad z_1 = ${lo} \\quad z_2 = ${hi}`
          : `z_{1,2} = \\frac{-b \\pm \\sqrt{\\Delta}}{2a} = ${reTex ? `${reTex} \\pm ` : '\\pm '}${s}`,
      say: 'Δ > 0 : deux racines réelles distinctes.',
    });
    return {
      delta,
      roots: [
        { a: re.p / re.q - root, b: 0 },
        { a: re.p / re.q + root, b: 0 },
      ],
      rootTex: [lo, hi],
      steps,
    };
  }
  // partie imaginaire : « 2i », « i », « i\frac{\sqrt{3}}{2} »
  const imTex = r === 1 ? (s === '1' ? 'i' : `${s}i`) : `i\\,${s}`;
  // Δ = (ik√r)² : « (4i)^2 », « (2i\sqrt{3})^2 »
  const iRoot = r === 1 ? (k === 1 ? 'i' : `${k}i`) : `${k === 1 ? '' : k}i\\sqrt{${r}}`;
  steps.push({
    tex: `\\Delta = ${delta} = (${iRoot})^2`,
    say: 'Δ < 0 : pas de racine réelle. Mais Δ est le carré d’un imaginaire pur, d’où deux racines complexes conjuguées.',
  });
  steps.push({
    tex: `z_{1,2} = \\frac{-b \\pm i\\sqrt{-\\Delta}}{2a} = ${reTex ? `${reTex} \\pm ` : '\\pm '}${imTex}`,
    say: 'Les deux racines sont conjuguées : symétriques par rapport à l’axe des réels.',
  });
  return {
    delta,
    roots: [
      { a: re.p / re.q, b: -root },
      { a: re.p / re.q, b: root },
    ],
    rootTex: [join(reTex, '-', imTex), join(reTex, '+', imTex)],
    steps,
  };
}

/** « re ± t » sans partie réelle nulle : « -1 - 2i », « 2i », « -\sqrt{2} » */
function join(reTex: string, sign: '+' | '-', t: string): string {
  if (!reTex) return sign === '-' ? `-${t}` : t;
  return `${reTex} ${sign} ${t}`;
}

/** az² + bz + c = 0 écrit proprement : « z^2 - 2z + 5 = 0 », « -z^2 + 3 = 0 » */
export function quadTex(a: number, b: number, c: number): string {
  const term = (coef: number, x: string, first: boolean) => {
    if (coef === 0) return '';
    const abs = Math.abs(coef);
    const body = x && abs === 1 ? x : `${abs}${x}`;
    if (first) return coef < 0 ? `-${body}` : body;
    return ` ${coef < 0 ? '-' : '+'} ${body}`;
  };
  const head = term(a, 'z^2', true);
  return `${head}${term(b, 'z', !head)}${term(c, '', false)} = 0`;
}

/** somme et produit des racines, et factorisation a(z − z₁)(z − z₂) */
export function vieta(a: number, b: number, c: number): Step[] {
  const { delta, rootTex } = solveQuadratic(a, b, c);
  const lead = a === 1 ? '' : a === -1 ? '-' : String(a);
  const factor = (t: string) => {
    if (t === '0') return 'z';
    // un seul terme : z − 2, z + 1{,}5, z + \sqrt{2}, z − 2i
    if (!/ [+-] /.test(t)) return t.startsWith('-') ? `(z + ${t.slice(1)})` : `(z - ${t})`;
    return `\\left(z - \\left(${t}\\right)\\right)`;
  };
  const steps: Step[] = [];
  if (delta !== 0)
    steps.push({
      tex: `z_1 + z_2 = -\\frac{b}{a} = ${fracTex(-b, a)} \\qquad z_1 z_2 = \\frac{c}{a} = ${fracTex(c, a)}`,
      say:
        delta < 0
          ? 'La somme et le produit de deux conjugués sont réels : 2 Re(z) et |z|².'
          : 'Somme et produit des racines, sans les calculer.',
    });
  const f =
    delta === 0
      ? `${lead}${factor(rootTex[0]!)}^2`
      : `${lead}${[factor(rootTex[0]!), factor(rootTex[1]!)]
          .sort((x, y) => Number(y === 'z') - Number(x === 'z'))
          .join('')}`;
  steps.push({
    tex: `${quadTex(a, b, c).replace(' = 0', '')} = ${f}`,
    say: delta < 0 ? 'Factorisation dans ℂ (impossible dans ℝ).' : 'Factorisation.',
  });
  return steps;
}

/* ------------------------------------------------------------------ */
/* z ↦ az + b                                                          */
/* ------------------------------------------------------------------ */

/** arguments réglables de a : multiples de 30° et de 45° dans ]−π ; π] (pas de 15°) */
export const ARG_STEPS = [-10, -9, -8, -6, -4, -3, -2, 0, 2, 3, 4, 6, 8, 9, 10, 12] as const;

export type MapKind = 'identity' | 'translation' | 'rotation' | 'homothety' | 'similarity';

export interface MapInfo {
  kind: MapKind;
  /** a en nombres (pour la figure) */
  a: Z;
  /** a = r e^{iθ} = … */
  aTex: string;
  /** centre Ω (point fixe), sauf translation et identité */
  center: Z | null;
  centerTex: string | null;
  say: string;
}

const approxTex = (v: number) => {
  const r = Number(v.toFixed(2));
  return String(Object.is(r, -0) ? 0 : r).replace('.', '{,}');
};

/** image de z par z ↦ az + b (en nombres) */
export const applyMap = (a: Z, b: Z, z: Z): Z => ({
  a: a.a * z.a - a.b * z.b + b.a,
  b: a.a * z.b + a.b * z.a + b.b,
});

/**
 * a = r e^{iθ} (r multiple de 0,5, θ = k15 × 15°), b sur le quadrillage. Nature
 * de la transformation, point fixe ω = b / (1 − a), au niveau de l'élève :
 * « similitude directe » en prépa seulement.
 */
export function affineMap(r: number, k15: number, b: Z, rank: number): MapInfo {
  const ex = exactValues(k15)!;
  // valeurs exactes (cos et sin aux angles droits : −1, 0, 1)
  const right = k15 % 6 === 0;
  const a: Z = right
    ? { a: r * Math.round(ex.cos.value), b: r * Math.round(ex.sin.value) }
    : { a: r * ex.cos.value, b: r * ex.sin.value };
  const th = radianLabel(k15);
  const rT = r === 1 ? '' : numTex(r);
  let aTex: string;
  if (k15 === 0) aTex = `a = ${numTex(r)}`;
  else if (right) aTex = `a = ${rT}${expTex(th.tex)} = ${zTex(a)}`;
  else
    aTex = `a = ${rT}${expTex(th.tex)} = ${rT ? `${rT}\\left(${unitTex(k15)}\\right)` : unitTex(k15)}`;
  const thPlain = th.plain;
  const rPlain = numTex(r).replace('{,}', ',');
  if (r === 1 && k15 === 0) {
    const zero = b.a === 0 && b.b === 0;
    return {
      kind: zero ? 'identity' : 'translation',
      a,
      aTex,
      center: null,
      centerTex: null,
      say: zero
        ? 'f(z) = z : chaque point reste à sa place.'
        : `f(z) = z + b : la translation de vecteur d’affixe b = ${zPlain(b)}. Pas de point fixe.`,
    };
  }
  let center: Z;
  let centerTex: string;
  const formula = '\\omega = a\\omega + b \\iff \\omega = \\frac{b}{1 - a}';
  if (b.a === 0 && b.b === 0) {
    center = { a: 0, b: 0 };
    centerTex = `${formula} = 0`;
  } else if (right) {
    const q = operate('quotient', b, { a: 1 - a.a, b: -a.b })!;
    center = toZ(q);
    centerTex = `${formula} = ${zTexQ(q.re, q.im)}`;
  } else {
    const den = (1 - a.a) ** 2 + a.b ** 2;
    center = {
      a: (b.a * (1 - a.a) - b.b * a.b) / den,
      b: (b.b * (1 - a.a) + b.a * a.b) / den,
    };
    const im = approxTex(Math.abs(center.b));
    const reT = approxTex(center.a);
    const imT = im === '0' ? '' : `${im}i`;
    const body = !imT
      ? reT
      : reT === '0'
        ? `${center.b < 0 ? '-' : ''}${imT}`
        : `${reT} ${center.b < 0 ? '-' : '+'} ${imT}`;
    centerTex = `${formula} \\approx ${body}`;
  }
  const atO = b.a === 0 && b.b === 0;
  const c = atO ? 'O' : 'Ω';
  let kind: MapKind;
  let say: string;
  if (k15 === 0 || k15 === 12) {
    kind = r === 1 ? 'rotation' : 'homothety';
    say =
      r === 1
        ? `a = −1 : la symétrie de centre ${c} (rotation d’angle π).`
        : `a est réel : l’homothétie de centre ${c} et de rapport ${k15 === 0 ? '' : '−'}${rPlain}.`;
  } else if (r === 1) {
    kind = 'rotation';
    say = `|a| = 1 : la rotation de centre ${c} et d’angle ${thPlain}.`;
  } else {
    kind = 'similarity';
    say =
      rank >= 8
        ? `La similitude directe de centre ${c}, de rapport ${rPlain} et d’angle ${thPlain}.`
        : `On tourne de ${thPlain} autour de ${c}, puis on multiplie les distances à ${c} par ${rPlain} : rotation et homothétie de même centre.`;
  }
  return { kind, a, aTex, center, centerTex, say };
}
